// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Modal, { ModalPresentationProps, SizeVariant } from "@insite/mobius/Modal";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@west/App/Components/Common/XngZone";
import Button, { ButtonPresentationProps } from "@west/MobiusOverrides/Button";
import { XNGAGE_CUSTOM_WIDGETS } from "@west/XDL/constants";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    sizeOfModal = "sizeOfModal",
    positionOfModalHorizontally = "positionOfModalHorizontally",
    positionOfModalVertically = "positionOfModalVertically",
    delayTime = "delayTime",
    acceptButtonText = "acceptButtonText",
    cancelButtonText = "cancelButtonText",
    preventPopupClose = "preventPopupClose",
    showOnce = "showOnce",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.sizeOfModal]: SizeVariant;
        [fields.positionOfModalHorizontally]: string;
        [fields.positionOfModalVertically]: string;
        [fields.delayTime]: number;
        [fields.acceptButtonText]: string;
        [fields.cancelButtonText]: string;
        [fields.preventPopupClose]: boolean;
        [fields.showOnce]: boolean;
    };
}

interface PopUpStyles {
    modal?: ModalPresentationProps;
    actionsWrapperButtons?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    confirmButton?: ButtonPresentationProps;
}

export const popUpStyles: PopUpStyles = {};

type Props = OwnProps;

const PopUp: React.FC<Props> = ({ id, fields }) => {
    const {
        title,
        sizeOfModal,
        positionOfModalHorizontally,
        positionOfModalVertically,
        delayTime,
        acceptButtonText,
        cancelButtonText,
        preventPopupClose,
        showOnce,
    } = fields;

    const styles = popUpStyles;
    const { isInShell } = useShellContext();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const popUpID = `${id}-xng-popup`;

    const closeModal = useCallback(() => {
        setModalIsOpen(false);
    }, []);

    const acceptModal = useCallback(() => {
        if (showOnce) {
            localStorage.setItem(popUpID, "true");
        }
        closeModal();
    }, [showOnce, popUpID, closeModal]);

    const openModal = useCallback(() => setModalIsOpen(true), []);

    const shouldSkipModal = useCallback((id: string, showOnce: boolean) => {
        const shown = localStorage.getItem(id);
        if ((showOnce && shown) || isInShell) {
            return true;
        }
        localStorage.removeItem(id);
        return false;
    }, []);

    useEffect(() => {
        if (shouldSkipModal(popUpID, showOnce)) {
            return;
        }

        const timer = setTimeout(openModal, delayTime * 1000);
        return () => clearTimeout(timer);
    }, [delayTime, showOnce, openModal, popUpID]);

    const renderButtons = () => {
        if (!acceptButtonText && !cancelButtonText) {
            return null;
        }

        return (
            <StyledWrapper {...styles.actionsWrapperButtons}>
                {cancelButtonText && (
                    <Button
                        {...styles.cancelButton}
                        onClick={closeModal}
                        data-test-selector={`${popUpID}-cancel-button`}
                        aria-label={String(cancelButtonText)}
                    >
                        {cancelButtonText}
                    </Button>
                )}
                {acceptButtonText && (
                    <Button
                        {...styles.confirmButton}
                        onClick={acceptModal}
                        data-test-selector={`${popUpID}-accept-button`}
                        aria-label={String(acceptButtonText)}
                    >
                        {acceptButtonText}
                    </Button>
                )}
            </StyledWrapper>
        );
    };

    return (
        <>
            {isInShell && <Button onClick={openModal}>{translate("Edit Popup Content")}</Button>}
            <Modal
                id={popUpID}
                headline={String(title)}
                isOpen={modalIsOpen}
                isCloseable={isInShell || !preventPopupClose}
                handleClose={closeModal}
                data-test-selector={popUpID}
                sizeVariant={sizeOfModal as SizeVariant}
                cssOverrides={{
                    modalContainer: css`
                        justify-content: ${positionOfModalHorizontally};
                        align-items: ${positionOfModalVertically};
                    `,
                }}
                {...styles.modal}
            >
                <XngZone contentId={id} zoneName="Popup Content" />
                {renderButtons()}
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: PopUp,
    definition: {
        group: XNGAGE_CUSTOM_WIDGETS,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Modal Title",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
                sortOrder: 0,
            },
            {
                name: fields.sizeOfModal,
                displayName: "Size Of Modal",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Tiny",
                        value: "small",
                    },
                    {
                        displayName: "Medium",
                        value: "medium",
                    },
                    {
                        displayName: "Large",
                        value: "large",
                    },
                ],
                defaultValue: "small",
                fieldType: "General",
                isRequired: true,
                sortOrder: 1,
            },
            {
                name: fields.positionOfModalHorizontally,
                displayName: "Position Of Modal Horizontally",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Left",
                        value: "flex-start",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "flex-end",
                    },
                ],
                defaultValue: "center",
                fieldType: "General",
                isRequired: true,
                sortOrder: 2,
            },
            {
                name: fields.positionOfModalVertically,
                displayName: "Position Of Modal Vertically",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Top",
                        value: "flex-start",
                    },
                    {
                        displayName: "Middle",
                        value: "center",
                    },
                    {
                        displayName: "Bottom",
                        value: "flex-end",
                    },
                ],
                defaultValue: "center",
                fieldType: "General",
                isRequired: true,
                sortOrder: 3,
            },
            {
                name: fields.delayTime,
                displayName: "Delay Time In Seconds",
                editorTemplate: "IntegerField",
                defaultValue: 5,
                fieldType: "General",
                isRequired: true,
                sortOrder: 4,
            },
            {
                name: fields.acceptButtonText,
                displayName: "Accept Button Text",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                sortOrder: 5,
            },
            {
                name: fields.cancelButtonText,
                displayName: "Cancel Button Text",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                sortOrder: 6,
            },
            {
                name: fields.preventPopupClose,
                displayName: "Prevent Popup Close",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showOnce,
                displayName: "Show Once",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 8,
            },
        ],
    },
};

export default widgetModule;
